<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Prop, Watch } from '@/helpers/Decorators';
import Pager from '@/helpers/Pager';
import { RelatedDocumentsContract } from '.';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { DetailsBuilderContract } from '@/components/builder/details';
import { KeyValuePair } from '@/helpers/Interfaces';
import FormsService from '@/modules/studio/forms/services/FormsService';
import RelatedDocuments from '@/modules/low-code/components/RelatedDocuments.vue';
import properties from '../../properties';
import FieldConfigsService, { FieldModel } from '@/modules/low-code/services/FieldConfigsService';
import ColumnsCustomizer from '@/components/common/dynamic-grid/ColumnsCustomizer.vue';
import FieldFilters from '@/components/forms/properties/FieldFilters.vue';
import FieldChoice from '../../properties/FieldChoice.vue';
import { Option } from '@/helpers/Interfaces';
import { AlwaysChoice } from '@/components/builder/form/enums/AlwaysChoice';
import { NeverChoice } from '@/components/builder/form/enums/NeverChoice';
import SummaryCustomizer from '@/components/common/dynamic-grid/SummaryCustomizer.vue';
import { kebabCase } from 'lodash';
import AutocompleteService from '@/modules/core/common/services/AutocompleteService';
import FieldMap from '@/components/forms/properties/FieldMap.vue';


@Options({
    name: 'RelatedDocumentsBlueprint',
    components: {
        RelatedDocuments,
        ColumnsCustomizer,
        FieldFilters,
        FieldChoice,
        FieldMap,
        ...properties,
        SummaryCustomizer
    },
})
export default class RelatedDocumentsBlueprint extends Vue
{
    @Prop({ default: null }) public details!: DetailsBuilderContract;
    @Prop({ default: null }) public parent!: AggregateBlueprint;
    @Prop({ default: null }) public blueprint!: RelatedDocumentsContract;

    public moduleOptions: any[] = [];
    public selectedModules: KeyValuePair[] = [];
    public filters: any = null;
    public filterValueOptions: (KeyValuePair & { type?: string })[] = [];
    public filterKeyOptions: (KeyValuePair & { type?: string })[] = [];

    public get licence(): string
    {
        return this.$route.params.licence as string;
    }

    public get publicId(): string
    {
        return this.$route.params.publicId as string ?? this.$route.params.moduleId as string;
    }

    public get design(): boolean
    {
        return this.details.designMode();
    }

    public get isColumns(): boolean
    {
        return this.blueprint.modules.length == 1;
    }

    public get columnsArray(): FieldModel[]
    {
        const result = this.blueprint.columns != null
            ? Object.values(this.blueprint.columns).filter(p => p.visibility).orderBy(p => p.order).toArray()
            : [];

        return result;
    }

    public get columnsKey(): string
    {
        if (!this.blueprint.columns)
            return 'no-columns';

        return JSON.stringify(
            Object.entries(this.blueprint.columns).map(([key, value]) => ({
                key,
                visibility: value.visibility,
                order: value.order
            }))
        );
    }

    public get AlwaysChoice(): AlwaysChoice
    {
        return AlwaysChoice.Always;
    }

    public get NeverChoice(): NeverChoice
    {
        return NeverChoice.Never;
    }

    public get gridHeaderSettingOptions(): Option<AlwaysChoice | NeverChoice>[]
    {
        return [
            { value: AlwaysChoice.Always, text: '[[[Zawsze]]]' },
            { value: NeverChoice.Never, text: '[[[Nigdy]]]' },
        ];
    }

    public async created(): Promise<void>
    {
        if (this.design)
        {
            await this.fetchModuleOptions();
            await this.fetchFilterKeyOptions();
            this.selectedModules = this.blueprint.modules?.map?.((p) => this.moduleOptions.find((x) => x.key == p));
            this.filters = this.blueprint.filtersJson ? JSON.parse(this.blueprint.filtersJson) : null;

            if (this.selectedModules.length == 1)
            {
                await this.fetchFilterValueOptions();
            }

            this.blueprint.filterMappings ??= {};
        }
    }

    public updated(): void
    {
        this.updateModule(this.selectedModules);
    }

    public async fetchModuleOptions(): Promise<void>
    {
        this.moduleOptions = (await FormsService.getModuleOptions(new Pager(1, 999))).items.map((p) => p.result);
    }

    public async fetchFilterValueOptions(): Promise<void>
    {
        const response = await AutocompleteService.fetchOptionsByCustomEndpoint(
            `admin/forms/module-fields/${kebabCase(this.selectedModules[0]?.key)}`,
            null,
            new Pager(1, 999, '', 'ASC'),
            null, null,
            { filterOnly: true }
        );

        if (!response) return;

        this.filterValueOptions = response.items
            .map((item) => item.result)
            .map((p) => ({ ...p, value: `${p.value} (${p.key})` }));
    }

    public async fetchFilterKeyOptions(): Promise<void>
    {
        const moduleId = await FormsService.getModuleId(this.publicId);

        const response = await AutocompleteService.fetchOptionsByCustomEndpoint(
            `admin/forms/module-fields/${moduleId}`,
            null,
            new Pager(1, 999, '', 'ASC'),
            null, null,
            { filterOnly: true }
        );

        if (!response) return;

        this.filterKeyOptions = response.items
            .map((item) => item.result)
            .map((p) => ({ ...p, value: `${p.value} (${p.key})` }));
    }

    public async getColumns(moduleId: string): Promise<void>
    {
        if (!moduleId)
        {
            this.blueprint.columns = null;

            return;
        }

        try
        {
            const columns = await FieldConfigsService.getFieldConfigDefaultSettings(
                moduleId,
                'Grid',
                'search'
            );

            this.blueprint.columns = columns;
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
            });
        }
    }

    @Watch('selectedModules')
    public async updateModules(): Promise<void>
    {
        this.blueprint.modules = this.selectedModules?.map?.((p) => p.key);
    }

    @Watch('filters', { deep: true })
    public async updateFiltersJson(): Promise<void>
    {
        this.blueprint.filtersJson = this.filters ? JSON.stringify(this.filters) : null;
    }

    // @Watch('blueprint.columns', { deep: false })
    // public onColumnsChanged(): void
    // {
    //     console.log('columns changed');
    //     this.$forceUpdate();
    // }

    public async updateModule(value: KeyValuePair[]): Promise<void>
    {
        if (value.length == 1)
        {
            await this.fetchFilterValueOptions();
            await this.getColumns(value[0].key);
        }
        else
        {
            this.filterValueOptions = [];
            await this.getColumns(null);
        }
    }
}
</script>

<template>
    <dynamic-details-component-wrapper :details="details" :parent="parent" :blueprint="blueprint">
        <template #default>
            <related-documents
                v-if="!design"
                :key="columnsKey"
                :blueprint-name="blueprint.name"
                :document-id="publicId"
                :parent-type="licence"
                :allow-bindings="blueprint.allowBindings"
                :blueprint-id="blueprint.id"
                :grid-header-setting="blueprint.gridHeaderSetting"
                :show-filters="blueprint.showFilters"
                :show-export="blueprint.showExport"
                :title="blueprint.title"
                :columns="blueprint.columns"
            />
            <div v-else>
                <list-view v-if="!isColumns" :items="[]" :preload="false">
                    <template #row>
                        <list-view-item :title="$t('[[[Data utworzenia]]]')" />
                        <list-view-item :title="$t('[[[Autor]]]')" />
                        <list-view-item :title="$t('[[[Moduł]]]')" />
                        <list-view-item :title="$t('[[[Tytuł]]]')" />
                        <list-view-item :title="$t('[[[Spółka]]]')" />
                        <list-view-item :title="$t('[[[Rejestr]]]')" />
                        <list-view-item :title="$t('[[[Sygnatura]]]')" />
                        <list-view-item :title="$t('[[[Status]]]')" />
                    </template>
                </list-view>
                <list-view v-else :items="[]" :preload="false">
                    <template #row>
                        <list-view-item v-for="(column, i) in columnsArray" :key="i" :title="column.headerName" />
                    </template>
                </list-view>
            </div>
        </template>
        <template #properties>
            <dynamic-details-accordion>
                <dynamic-details-accordion-item
                    icon="far fa-cog"
                    :header="$t('[[[Konfiguracja]]]')"
                    :open="true"
                >
                    <field-name v-model="blueprint.name" />
                    <ideo-form-group>
                        <ideo-form-checkbox v-model="blueprint.allowBindings">
                            {{ $t('[[[Pokaż fukncję dowiązania dokumentów wg uprawnień]]]') }}
                        </ideo-form-checkbox>
                    </ideo-form-group>
                    <ideo-form-group :label="$t('[[[Moduły]]]')">
                        <ideo-select v-model="selectedModules" :options="moduleOptions" :multiselect="true" @update:model-value="updateModule" />
                    </ideo-form-group>
                    <ideo-form-group :label="$t('[[[Kto może wiązać dokumenty]]]')">
                        <ideo-form-checkbox v-model="blueprint.allowAuthor">
                            {{ $t('[[[Autor dokumentu]]]') }}
                        </ideo-form-checkbox>
                        <ideo-form-checkbox v-model="blueprint.allowTargets">
                            {{ $t('[[[Osoby które realizują obecnie zadanie w obiegu dokumentu]]]') }}
                        </ideo-form-checkbox>
                        <ideo-form-checkbox v-model="blueprint.allowAll">
                            {{ $t('[[[Osoby które mają dostęp do dokumentu]]]') }}
                        </ideo-form-checkbox>
                    </ideo-form-group>

                    <template v-if="selectedModules.length === 1">
                        <ideo-form-group>
                            <portal-target name="view-manager-summary" />
                            <summary-customizer
                                :summary-prop="'summary'"
                                :columns="blueprint.columns"
                                portal-name="view-manager-summary"
                                title="[[[Podsumowanie kolumn filtrowanych wyników]]]"
                            />
                        </ideo-form-group>


                        <ideo-form-group>
                            <portal-target name="view-manager-page-summary" />
                            <summary-customizer
                                :summary-prop="'pageSummary'"
                                :columns="blueprint.columns"
                                portal-name="view-manager-page-summary"
                                title="[[[Podsumowanie kolumn bieżącej strony]]]"
                            />
                        </ideo-form-group>
                    </template>

                    <field-visible :details="details" :blueprint="blueprint" />

                    <field-choice v-model="blueprint.gridHeaderSetting" :options="gridHeaderSettingOptions" :label="$t('[[[Widoczność nagłówka]]]')" />

                    <div v-show="blueprint.gridHeaderSetting === AlwaysChoice">
                        <ideo-form-localize v-slot="{ locale }">
                            <field-text v-model="blueprint.title[locale]" :label="$t('[[[Tytuł]]]')" />
                        </ideo-form-localize>

                        <field-checkbox v-model="blueprint.showExport" :label="$t('[[[Wyświetlaj eksport]]]')" />
                        <field-checkbox v-model="blueprint.showFilters" :label="$t('[[[Wyświetlaj filtry]]]')" />
                    </div>

                    <div v-if="isColumns">
                        <portal-target name="view-manager-columns" ref="target" />
                        <columns-customizer :columns="blueprint.columns" />
                    </div>
                </dynamic-details-accordion-item>
                <dynamic-details-accordion-item v-if="isColumns" icon="far fa-filter" :header="$t('[[[Filtry]]]')">
                    <field-filters
                        v-model="filters"
                        :selected-modules="selectedModules"
                        :label="$t('[[[Filtry]]]')"
                    />
                    <field-map
                        v-model="blueprint.filterMappings"
                        :label="$t('[[[Filtry wartościami pól formularza]]]')"
                        :blueprint="blueprint"
                        :key-fields="filterKeyOptions"
                        :value-fields="filterValueOptions"
                        :selected-modules="selectedModules"
                        is-module-filters
                    />
                </dynamic-details-accordion-item>
            </dynamic-details-accordion>
        </template>
    </dynamic-details-component-wrapper>
</template>
